<template>
    <div class="bg-black rounded-lg shadow-md text-white p-4 mt-6">
        <h1 class="text-2xl font-black font-lato text-left mb-6 uppercase">Detailed Reef Analysis</h1>

        <div v-if="isLoading" class="flex items-center justify-center py-12">
            <div class="text-center">
                <i class="mdi mdi-loading mdi-spin text-3xl text-primary mb-3"></i>
                <p class="text-neutral-400">Loading detailed reef statistics...</p>
            </div>
        </div>

        <div v-else-if="hasError" class="flex items-center justify-center py-12">
            <div class="text-center">
                <i class="mdi mdi-alert-circle text-3xl text-red-400 mb-3"></i>
                <p class="text-neutral-400">Unable to load detailed reef statistics</p>
            </div>
        </div>

        <div v-else class="space-y-6">
            <!-- Detailed Coral Breakdown -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Live Coral Types -->
                <div class="bg-neutral-800 rounded-lg p-4">
                    <h2 class="text-lg font-black font-lato text-white mb-4 uppercase">
                        Live Coral
                    </h2>
                    <div class="space-y-2">
                        <div v-for="coral in liveCoralTypes" :key="coral.name" class="flex justify-between items-center py-1">
                            <div class="flex items-center">
                                <div class="w-3 h-3 rounded-full mr-3" :style="{ backgroundColor: coral.color }"></div>
                                <span class="text-sm text-white">{{ coral.displayName }}</span>
                            </div>
                            <div class="text-right">
                                <div class="text-sm font-black font-lato text-white">{{ coral.percent.toFixed(1) }}%</div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-4 pt-3 border-t border-neutral-700">
                        <div class="flex justify-between items-center">
                            <span class="text-sm font-black font-lato text-white uppercase">Total Live</span>
                            <div class="text-right">
                                <div class="text-lg font-black font-lato text-white">{{ stats.liveCoral.percent }}%</div>
                                <div class="text-xs text-neutral-400">{{ stats.liveCoral.area.toFixed(1) }} m²</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Dead/Bleached Coral -->
                <div class="bg-neutral-800 rounded-lg p-4">
                    <h2 class="text-lg font-black font-lato text-white mb-4 uppercase">
                        Dead/Bleached
                    </h2>
                    <div class="space-y-2">
                        <div v-for="coral in deadCoralTypes" :key="coral.name" class="flex justify-between items-center py-1">
                            <div class="flex items-center">
                                <div class="w-3 h-3 rounded-full mr-3" :style="{ backgroundColor: coral.color }"></div>
                                <span class="text-sm text-white">{{ coral.displayName }}</span>
                            </div>
                            <div class="text-right">
                                <div class="text-sm font-black font-lato text-white">{{ coral.percent.toFixed(1) }}%</div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-4 pt-3 border-t border-neutral-700">
                        <div class="flex justify-between items-center">
                            <span class="text-sm font-black font-lato text-white uppercase">Total Dead</span>
                            <div class="text-right">
                                <div class="text-lg font-black font-lato text-white">{{ stats.deadCoral.percent }}%</div>
                                <div class="text-xs text-neutral-400">{{ stats.deadCoral.area.toFixed(1) }} m²</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Substrate Composition -->
            <div class="bg-neutral-800 rounded-lg p-4">
                <h2 class="text-lg font-black font-lato text-white mb-4 uppercase">
                    Substrate Composition
                </h2>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div v-for="substrate in substrateTypes" :key="substrate.name" class="bg-black p-3 rounded-lg text-center">
                        <div class="text-lg font-black font-lato text-white mb-1">{{ substrate.percent.toFixed(1) }}%</div>
                        <div class="text-xs text-neutral-400 uppercase">{{ substrate.displayName }}</div>
                    </div>
                </div>
            </div>

            <!-- All Details - Collapsible Section -->
            <div class="bg-neutral-800 rounded-lg p-4">
                <div class="flex items-center justify-between cursor-pointer" @click="showAllClasses = !showAllClasses">
                    <h2 class="text-lg font-black font-lato text-white uppercase">
                        All Details
                    </h2>
                    <i :class="['mdi', showAllClasses ? 'mdi-chevron-up' : 'mdi-chevron-down', 'text-xl text-neutral-400 transition-transform duration-200']"></i>
                </div>
                
                <div v-if="showAllClasses" class="mt-4 space-y-4">
                    <!-- Survey Details -->
                    <div>
                        <h3 class="text-sm font-black font-lato text-white uppercase mb-3">Survey Details</h3>
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div class="bg-black p-3 rounded-lg text-center">
                                <div class="text-lg font-black font-lato text-white">{{ formattedNumber(stats.totalVertices) }}</div>
                                <div class="text-xs text-neutral-400 uppercase">Vertices</div>
                            </div>
                            <div class="bg-black p-3 rounded-lg text-center">
                                <div class="text-lg font-black font-lato text-white">{{ stats.totalSurfaceArea.toFixed(1) }}</div>
                                <div class="text-xs text-neutral-400 uppercase">Surface Area m²</div>
                            </div>
                            <div class="bg-black p-3 rounded-lg text-center">
                                <div class="text-lg font-black font-lato text-white">{{ stats.suitableSubstrate.toFixed(1) }}</div>
                                <div class="text-xs text-neutral-400 uppercase">Suitable m²</div>
                            </div>
                            <div class="bg-black p-3 rounded-lg text-center">
                                <div class="text-lg font-black font-lato text-white">{{ Object.keys(rawData).length }}</div>
                                <div class="text-xs text-neutral-400 uppercase">Categories</div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- All Classes -->
                    <div>
                        <h3 class="text-sm font-black font-lato text-white uppercase mb-3">All Classes</h3>
                        <div class="space-y-2 max-h-60 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                            <div v-for="item in allClasses" :key="item.name" class="flex justify-between items-center py-2 text-sm border-b border-neutral-700 last:border-b-0">
                                <span class="text-neutral-300">{{ item.displayName }}</span>
                                <div class="text-right">
                                    <div class="text-white font-black font-lato">{{ item.percent.toFixed(1) }}%</div>
                                    <div class="text-xs text-neutral-400">{{ item.area.toFixed(1) }} m²</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { storage_resto } from '../main';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';

export default {
    name: 'SurveyStatsAll',
    props: {
        dataPath: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            rawData: {},
            isLoading: true,
            hasError: false,
            showAllClasses: false
        };
    },
    mounted() {
        this.loadStatistics();
    },
    computed: {
        stats() {
            if (!this.rawData || Object.keys(this.rawData).length === 0) {
                return this.getEmptyStats();
            }

            // Parse basic metrics
            const totalVertices = parseInt(this.rawData.total_vertices) || 0;
            const totalSurfaceArea = parseFloat(this.rawData.total_surface_area) || 0;
            const liveCoralArea = parseFloat(this.rawData.total_live_coral_area) || 0;
            
            // Calculate percent coral cover if not provided
            const percentCoralCover = totalSurfaceArea > 0 
                ? parseFloat(((liveCoralArea / totalSurfaceArea) * 100).toFixed(1))
                : 0;
            
            const liveCoral = {
                area: liveCoralArea,
                percent: percentCoralCover
            };
            const suitableSubstrate = parseFloat(this.rawData.suitable_substrate_area) || 0;

            // Calculate algae cover
            const algaeCover = {
                area: parseFloat(this.rawData.class_algae_covered_substrate_area) || 0,
                percent: parseFloat((parseFloat(this.rawData.class_algae_covered_substrate_area_percent) || 0).toFixed(1))
            };

            // Calculate dead coral totals
            const deadCoralArea = this.calculateDeadCoralArea();
            const deadCoral = {
                area: deadCoralArea,
                percent: totalSurfaceArea > 0 ? parseFloat(((deadCoralArea / totalSurfaceArea) * 100).toFixed(1)) : 0
            };

            return {
                totalVertices,
                totalSurfaceArea,
                percentCoralCover,
                liveCoral,
                deadCoral,
                algaeCover,
                suitableSubstrate
            };
        },

        liveCoralTypes() {
            const types = [
                { name: 'massive_meandering_alive', displayName: 'Massive/Meandering', color: '#27bdf4' },
                { name: 'other_coral_alive', displayName: 'Other Coral', color: '#1e95c2' },
                { name: 'meandering_alive', displayName: 'Meandering', color: '#166d8f' },
                { name: 'pocillopora_alive', displayName: 'Pocillopora', color: '#0e465d' },
                { name: 'stylophora_alive', displayName: 'Stylophora', color: '#06202b' },
                { name: 'acropora_alive', displayName: 'Acropora', color: '#051c25' },
                { name: 'branching_alive', displayName: 'Branching', color: '#041720' }
            ];

            return types
                .map(type => ({
                    ...type,
                    area: parseFloat(this.rawData[`class_${type.name}_area`]) || 0,
                    percent: parseFloat(this.rawData[`class_${type.name}_area_percent`]) || 0
                }))
                .filter(type => type.area > 0)
                .sort((a, b) => b.area - a.area);
        },

        deadCoralTypes() {
            const types = [
                { name: 'massive_meandering_dead', displayName: 'Massive/Meandering Dead', color: '#ee756f' },
                { name: 'massive_meandering_bleached', displayName: 'Massive/Meandering Bleached', color: '#dc6659' },
                { name: 'other_coral_dead', displayName: 'Other Coral Dead', color: '#ca5648' },
                { name: 'other_coral_bleached', displayName: 'Other Coral Bleached', color: '#b84738' },
                { name: 'branching_dead', displayName: 'Branching Dead', color: '#a63829' },
                { name: 'branching_bleached', displayName: 'Branching Bleached', color: '#942a1c' },
                { name: 'meandering_dead', displayName: 'Meandering Dead', color: '#821c10' },
                { name: 'meandering_bleached', displayName: 'Meandering Bleached', color: '#701007' }
            ];

            return types
                .map(type => ({
                    ...type,
                    area: parseFloat(this.rawData[`class_${type.name}_area`]) || 0,
                    percent: parseFloat(this.rawData[`class_${type.name}_area_percent`]) || 0
                }))
                .filter(type => type.area > 0)
                .sort((a, b) => b.area - a.area);
        },

        substrateTypes() {
            const types = [
                { name: 'unknown_hard_substrate', displayName: 'Hard Substrate', color: '#6b7280' },
                { name: 'sand', displayName: 'Sand', color: '#fbbf24' },
                { name: 'rubble', displayName: 'Rubble', color: '#9ca3af' },
                { name: 'dark', displayName: 'Dark Areas', color: '#374151' }
            ];

            return types
                .map(type => ({
                    ...type,
                    area: parseFloat(this.rawData[`class_${type.name}_area`]) || 0,
                    percent: parseFloat(this.rawData[`class_${type.name}_area_percent`]) || 0
                }))
                .filter(type => type.area > 0)
                .sort((a, b) => b.area - a.area);
        },

        allClasses() {
            if (!this.rawData || Object.keys(this.rawData).length === 0) {
                return [];
            }

            const classes = [];
            const processedKeys = new Set();

            // Go through all keys in rawData to find area and percent pairs
            Object.keys(this.rawData).forEach(key => {
                if (key.startsWith('class_') && key.endsWith('_area') && !key.includes('_vertices')) {
                    const baseKey = key.replace('_area', '');
                    const percentKey = `${baseKey}_area_percent`;
                    
                    if (!processedKeys.has(baseKey) && this.rawData[percentKey]) {
                        const area = parseFloat(this.rawData[key]) || 0;
                        const percent = parseFloat(this.rawData[percentKey]) || 0;
                        
                        if (area > 0) {
                            const className = baseKey.replace('class_', '');
                            const displayName = this.formatClassName(className);
                            
                            classes.push({
                                name: className,
                                displayName,
                                area,
                                percent
                            });
                            
                            processedKeys.add(baseKey);
                        }
                    }
                }
            });

            return classes.sort((a, b) => b.area - a.area);
        }
    },
    methods: {
        async loadStatistics() {
            try {
                this.isLoading = true;
                this.hasError = false;

                if (!this.dataPath) {
                    throw new Error('No dataPath provided');
                }

                // Get the download URL for parsed.txt from Firebase Storage
                const storagePath = `${this.dataPath}/output/parsed.txt`;
                console.log('Loading statistics from:', storagePath);
                
                const fileRef = storageRef(storage_resto, storagePath);
                const downloadUrl = await getDownloadURL(fileRef);
                
                const response = await fetch(downloadUrl);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const text = await response.text();
                this.parseStatisticsData(text);
                
            } catch (error) {
                console.error('Error loading statistics:', error);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },

        parseStatisticsData(text) {
            const lines = text.trim().split('\n');
            const data = {};
            
            lines.forEach(line => {
                if (line.trim()) {
                    const [key, value] = line.split(',');
                    data[key] = value;
                }
            });
            
            this.rawData = data;
        },

        calculateDeadCoralArea() {
            const deadTypes = [
                'massive_meandering_dead',
                'massive_meandering_bleached',
                'other_coral_dead',
                'other_coral_bleached',
                'branching_dead',
                'branching_bleached',
                'meandering_dead',
                'meandering_bleached'
            ];

            return deadTypes.reduce((total, type) => {
                return total + (parseFloat(this.rawData[`class_${type}_area`]) || 0);
            }, 0);
        },

        getEmptyStats() {
            return {
                totalVertices: 0,
                totalSurfaceArea: 0,
                percentCoralCover: 0,
                liveCoral: { area: 0, percent: 0 },
                deadCoral: { area: 0, percent: 0 },
                algaeCover: { area: 0, percent: 0 },
                suitableSubstrate: 0
            };
        },

        formattedNumber(number) {
            return new Intl.NumberFormat().format(number);
        },

        formatClassName(className) {
            return className
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }
    },
    watch: {
        dataPath: {
            handler() {
                // When dataPath changes, reload statistics
                this.loadStatistics();
            },
            immediate: false
        }
    }
};
</script>

<style scoped>
/* Custom styles for the survey statistics component */
.text-primary {
    color: #27bdf4;
}
</style>
